/**
 * Create Campaign wizard: the step order and what each step needs before
 * Next (redesign 2026-10-28, plan items N1 and N2). Kept free of React so the
 * gating per method is unit tested.
 *
 *   1 Details            name, goal (call to action) and its link, extra links
 *   2 Leads              optional
 *   3 Method             AI writes each message / Use a template / Your Claude
 *   4 Messages           depends on the method (samples, template text or timing only)
 *   5 What the AI knows  the messaging brief; every method must confirm it
 *   6 Preview and launch summary, preview by method, launch now or save as draft
 */

import { missingBriefFields, type CampaignBrief, type CtaType } from '@dealcloser/shared'
import { resourceRowsValid, type ResourceRow } from './campaign-resources'
import {
  slotValuesWithLink,
  templateProblems,
  type TemplateChoice,
  type WizardMessageWriter,
} from './templates/template-fill'
import type { SequenceStep } from './types'

export const WIZARD_STEPS = [
  'Details',
  'Leads',
  'Method',
  'Messages',
  'What the AI knows',
  'Preview and launch',
] as const

export const TOTAL_STEPS = WIZARD_STEPS.length

/** Step numbers (1-based), so the wizard never compares against bare digits. */
export const STEP = {
  details: 1,
  leads: 2,
  method: 3,
  messages: 4,
  brief: 5,
  launch: 6,
} as const

export const URL_PATTERN = /^https?:\/\/\S+\.\S+/

/** Whether the campaign goal (CTA) and its link are complete and valid. */
export function ctaFormValid(ctaType: CtaType, link: string): boolean {
  if (ctaType === 'direct_conversation') return true
  return URL_PATTERN.test(link.trim())
}

/** The slice of the wizard's state the gates read. */
export type WizardGateState = {
  readonly name: string
  readonly cta_type: CtaType
  readonly offer_link: string
  readonly resources: readonly ResourceRow[]
  readonly sequence_steps: readonly SequenceStep[]
  readonly message_writer: WizardMessageWriter
  readonly template_angle: TemplateChoice | null
  readonly template_slots: Readonly<Record<string, string>>
  readonly brief: CampaignBrief
  readonly brief_confirmed: boolean
}

export type WizardGateContext = {
  /** The user has a live Claude connection that may write (Settings > Claude). */
  readonly mcpAvailable: boolean
}

/** The link the campaign saves: none for Direct Conversation or when empty. */
export function campaignLinkFor(state: Pick<WizardGateState, 'cta_type' | 'offer_link'>): string | null {
  const link = state.offer_link.trim()
  return state.cta_type !== 'direct_conversation' && link ? link : null
}

/** Template mode needs a sequence picked and nothing left to fill (card S3). Other methods are always ready. */
export function templateReady(state: WizardGateState): boolean {
  if (state.message_writer !== 'template') return true
  const slots = slotValuesWithLink(state.template_slots, campaignLinkFor(state))
  return state.template_angle !== null && templateProblems(state.sequence_steps, slots).length === 0
}

/** Whether the user may move on from `step`. */
export function stepReady(step: number, state: WizardGateState, ctx: WizardGateContext): boolean {
  switch (step) {
    case STEP.details:
      // The link must be a real URL because the AI pastes it verbatim. Every
      // additional-resource row must be empty or complete.
      return state.name.trim().length > 0 && ctaFormValid(state.cta_type, state.offer_link) && resourceRowsValid(state.resources)
    case STEP.leads:
      return true // leads are optional
    case STEP.method:
      // Your Claude needs a connection that can write; the card is disabled
      // without one, and a restored draft must not slip past it.
      return state.message_writer !== 'mcp' || ctx.mcpAvailable
    case STEP.messages:
      // Writing samples are optional for AI writing and not used for Your
      // Claude; templates must be complete.
      return state.sequence_steps.length > 0 && templateReady(state)
    case STEP.brief:
      // Every method confirms the brief: DealCloser AI writes and replies
      // from it, template campaigns reply from it, and Your Claude reads it
      // through get_lead_brief.
      return state.brief_confirmed && missingBriefFields(state.brief).length === 0
    default:
      return true
  }
}

/** Final gate on Create: every step must pass (a restored draft can land straight on step 6). */
export function canCreate(state: WizardGateState, ctx: WizardGateContext): boolean {
  return [STEP.details, STEP.method, STEP.messages, STEP.brief].every((s) => stepReady(s, state, ctx))
}
